// validateForm — pure required-field + end-after-start validation.
//
// Extracted VERBATIM from `components/ManualTripCreator.js` (the `validateForm`
// helper) as part of the behavior-preserving refactor (issue #240). It walks the
// shared `REQUIRED_FIELDS` list in order and returns the FIRST blank field's
// message; if all required fields are present it then checks that the end date is
// not before the start date. It returns the error message string, or `null` when
// the form is valid — exactly as the source did (the container gates on a
// truthy return).
//
// The per-field precedence is observable and preserved by `REQUIRED_FIELDS`'
// ordering: tripName → startDate → startDestination → endDate → endDestination →
// end-after-start.
//
// Totality mirrors the source EXACTLY:
//   - The blank test is `trim ? !value.trim() : !value`, copied verbatim. The
//     source did NOT guard against a missing `formData` or non-string trimmed
//     values (every required field is a controlled string input), so NO extra
//     guard is added here.
//   - The date comparison uses `new Date(formData.startDate)` / `new
//     Date(formData.endDate)` and `end < start`, identical to the source. An
//     unparseable date yields an Invalid Date whose comparison is `false`, just
//     as before — no guard is added.
//   - Reads NO clock: both Dates parse the user-entered date strings, not the
//     current time, so no `now` injection is required.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import REQUIRED_FIELDS from './requiredFields.js';

/**
 * Validate the manual trip form data.
 *
 * @param {Object} formData - The current form values.
 * @returns {string|null} The first validation error message, or `null` when the
 *                        form passes all checks.
 */
export function validateForm(formData) {
  for (const { key, message, trim } of REQUIRED_FIELDS) {
    const value = formData[key];
    if (trim ? !value.trim() : !value) {
      return message;
    }
  }
  const start = new Date(formData.startDate);
  const end = new Date(formData.endDate);
  if (end < start) {
    return 'End date must be after start date';
  }
  return null;
}

export default validateForm;
